import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IChangeDialogResult, IChangeSet } from 'src/app/changes-dialog/changes-dialog.component';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  AnalyteTemplate,
  AssayName,
  ExperimentType,
  User,
} from 'src/app/services/labpartner.service.model';
import { EVENT_CREATE_ASSAY, EVENT_UPDATE_ASSAY } from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';

@Component({
  selector: 'app-assay-dialog',
  templateUrl: './assay-dialog.component.html',
  styleUrls: ['./assay-dialog.component.scss'],
  standalone: false,
})
export class AssayDialogComponent extends BaseComponent implements OnInit {
  fileName: string = '';
  user: User | undefined = undefined;

  assay: AssayName = {
    analyteTemplates: [] as AnalyteTemplate[],
  } as AssayName;

  constructor(
    private dialogService: DialogService,
    private labpartnerService: LabpartnerService,
    private notificationService: NotificationService,
    private loggingService: LoggingService,
    public dialogRef: MatDialogRef<AssayDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    super();
  }

  ngOnInit(): void {}

  // This method is called when the component is destroyed
  protected ngOnDestroyInternal(): void {}

  // This method is called when the user selects a file
  onFileChange(ev: any) {
    const reader = new FileReader();

    const file = ev.target.files[0] as File;
    this.fileName = file.name;

    if (!file.name.endsWith('.amf')) {
      return this.notifyWithErrorMessage('Uploaded file must be an AMF file.');
    }

    reader.onload = () => {
      const data = reader.result as string;

      // Remove checksum and parse to json
      let jsonData;
      try {
        jsonData = JSON.parse(data.split('\n').slice(1).join('\n'));

        this.assay = {
          assayId: jsonData.id,
          assayRevision: jsonData.revision,
          name: jsonData.name,
          shortName: jsonData.short_name,
          instrument: jsonData.assay_type,
          analyteTemplates: jsonData.targets.map((t: any) => {
            return {
              name: t.name,
              shortName: t.short_name,
              chamber: t.chamber,
              channel: t.channel,
              displaySeq: t.display_seq,
              groupId: t.group_id ? t.group_id : null,
            };
          }),
        };
        if (!this.assayIsValid()) {
          this.assayInvalidDialog();
        }
      } catch (ex) {
        this.assayInvalidDialog();
        return this.notifyWithErrorMessage('AMF file has incorrect format.');
      }
    };

    reader.readAsText(file);
  }

  // This method is called when the user clicks the cancel button
  cancel() {
    this.dialogRef.close();
  }

  // This method is called when the user clicks the save button
  save() {
    this.subscription.add(
      this.labpartnerService
        .getAssayNameByIdAndRevision(this.assay.assayId, this.assay.assayRevision)
        .subscribe((assay: AssayName) => {
          this.assay.id = assay.id;
          if (assay) {
            if (!this.compareAssays(assay, this.assay)) {
              this.dialogService
                .openConfirmDialog(
                  'An assay already exists with the same name, ID and revision.\n' +
                    'If you overwrite this existing assay AMF, past experiments with results\n' +
                    'that refer to this assay may not be found or show incorrectly in reports.\n' +
                    '\n' +
                    'Would you like to continue and overwrite the\n' +
                    'existing AMF?',
                  { width: '500px', panelClass: 'confirm-dialog-container-pre-wrap' },
                )
                .afterClosed()
                .subscribe(result => {
                  if (result) {
                    // Create a changeSet for the changes dialog
                    const changeSet: IChangeSet[] = [];
                    const properties = ['name', 'shortName', 'instrument'];
                    // Check if there are any changes to the assay
                    properties.forEach(prop => {
                      if ((this.assay as any)[prop] !== (assay as any)[prop]) {
                        changeSet.push({
                          identifier: `Assay ${assay.assayId}.${assay.assayRevision}`,
                          field: prop.charAt(0).toUpperCase() + prop.slice(1),
                          oldValue: (assay as any)[prop],
                          newValue: (this.assay as any)[prop],
                        });
                      }
                    });

                    // Check if there are any changes to the analyte templates
                    const minLength = Math.min(
                      this.assay.analyteTemplates.length,
                      assay.analyteTemplates.length,
                    );
                    for (let i = 0; i < minLength; i++) {
                      const template = this.assay.analyteTemplates[i] as any;
                      const existingTemplate = assay.analyteTemplates[i] as any;
                      const templateProperties = ['name', 'shortName', 'chamber', 'channel'];
                      templateProperties.forEach(prop => {
                        if (template[prop] != existingTemplate[prop]) {
                          changeSet.push({
                            identifier: `AnalyteTemplate ${i}`,
                            field: prop.charAt(0).toUpperCase() + prop.slice(1),
                            oldValue: existingTemplate[prop],
                            newValue: template[prop],
                          });
                        }
                      });
                    }

                    // Check if there are any new or removed templates
                    if (this.assay.analyteTemplates.length > assay.analyteTemplates.length) {
                      changeSet.push({
                        identifier: `AnalyteTemplates`,
                        field: 'New Analytes',
                        oldValue: assay.analyteTemplates.length + '',
                        newValue: this.assay.analyteTemplates.length + '',
                      });
                    } else if (this.assay.analyteTemplates.length < assay.analyteTemplates.length) {
                      changeSet.push({
                        identifier: `AnalyteTemplates`,
                        field: 'Removed Analytes',
                        oldValue: assay.analyteTemplates.length + '',
                        newValue: this.assay.analyteTemplates.length + '',
                      });
                    }

                    // Open the changes dialog
                    const changeDialogRef = this.dialogService.openConfirmChangesDialog(
                      changeSet,
                      ExperimentType.ValidationAndVerification,
                      { isDelete: false, multiReason: true },
                    );

                    //Subscribe to the dialog result
                    const changeLogSub = changeDialogRef.afterClosed().subscribe(async data => {
                      if (data?.submitClicked && data?.reasonProvided) {
                        this.createOrUpdateAssay(data);
                      }
                    });
                    this.subscription.add(changeLogSub);
                  } else {
                    this.notificationService.warn(':: Cancelled');
                  }
                });
            } else {
              this.notificationService.warn('No changes were made to the assay.');
            }
          } else {
            this.createOrUpdateAssay();
          }
        }),
    );
  }

  // This method is called to create or update the assay
  createOrUpdateAssay(data?: IChangeDialogResult) {
    this.subscription.add(
      this.labpartnerService.createOrUpateAssayName(this.assay, data).subscribe(
        (assay: AssayName) => {
          this.notificationService.success('Assay saved successfully.');
          const props: { [key: string]: string | number } = {
            Id: assay.id + '',
            AssayId: assay.assayId + '',
            AssayRevision: assay.assayRevision + '',
            UserId: this.user?.userId ?? 0,
          };
          if (data) {
            this.loggingService.logEvent(EVENT_UPDATE_ASSAY, props);
          } else {
            this.loggingService.logEvent(EVENT_CREATE_ASSAY, props);
          }

          this.dialogRef.close(true);
        },
        () => {
          this.notifyWithErrorMessage('Failed to save assay.');
        },
      ),
    );
  }

  assayInvalidDialog() {
    this.dialogService
      .openConfirmDialog('The AMF does not have the required data to store the assay.\n', {
        width: '500px',
        panelClass: 'confirm-dialog-container-pre-wrap',
        isOk: true,
      })
      .afterClosed()
      .subscribe(() => {});
  }

  // This method is called to determine if the assay is valid
  assayIsValid() {
    return (
      this.assay.assayId &&
      this.assay.assayRevision &&
      this.assay.name &&
      this.assay.shortName &&
      this.assay.instrument &&
      this.validateAnalyteTemplates()
    );
  }

  private notifyWithErrorMessage(msg: string): void {
    this.notificationService.error(msg);
    this.fileName = '';
    return;
  }

  private validateAnalyteTemplates(): boolean {
    return this.assay.analyteTemplates.every(
      t =>
        typeof t.name === 'string' &&
        t.name.trim().length > 0 &&
        typeof t.shortName === 'string' &&
        t.shortName.trim().length > 0 &&
        typeof t.chamber === 'number' &&
        t.chamber > 0 &&
        typeof t.channel === 'number' &&
        t.channel > 0,
    );
  }

  // This method is called to compare two assays
  private compareAssays(assay1: AssayName, assay2: AssayName): boolean {
    return (
      assay1.name === assay2.name &&
      assay1.shortName === assay2.shortName &&
      assay1.instrument === assay2.instrument &&
      assay1.analyteTemplates.length === assay2.analyteTemplates.length &&
      assay1.analyteTemplates.every((t, i) => {
        const t2 = assay2.analyteTemplates[i];
        return (
          t.name === t2.name &&
          t.shortName === t2.shortName &&
          t.chamber === t2.chamber &&
          t.channel === t2.channel
        );
      })
    );
  }
}
